type EntityItem = {
  post: any;
  parentComment?: null | NonNullable<unknown>;
  international: any;
  id: string;
  entity: string;
  subCategory?: { header?: string | null; id?: string | null; categoryId?: string | null };
  category?: { name?: string | null; id?: string | null };
  article?: { name?: string | null; id?: string | null };
  business?: { name?: string | null; id?: string | null; slug?: string | null };
  international_community?: { name?: string | null; id?: string | null };
  text?: string | null;
};

export type EntityKind =
  | 'subcategory'
  | 'category'
  | 'article'
  | 'business'
  | 'post'
  | 'international_community'
  | 'unknown'
  | 'reply';

export type EntityRelation = {
  title: string;
  kind: EntityKind;
  kindLabel: string;
  link: string;
};

type EntityMeta = {
  title: string;
  link: string;
  kind: EntityKind;
  kindLabel: string;
  relations: EntityRelation[];
};

const KIND_LABEL: Record<EntityKind, string> = {
  subcategory: 'Subcategory',
  category: 'Category',
  article: 'Article',
  business: 'Business',
  post: 'Post',
  international_community: 'International',
  unknown: 'Unknown',
  reply: 'Reply',
};

export function getPublicDomain(): string {
  const websiteUrl = process.env.REACT_APP_WEBSITE_URL || '';

  if (!websiteUrl) {
    console.error('REACT_APP_WEBSITE_URL is not set');

    return '';
  }

  try {
    const url = new URL(websiteUrl);

    return `${url.protocol}//${url.host}/`;
  } catch {
    console.error('Invalid REACT_APP_WEBSITE_URL value:', websiteUrl);

    return '';
  }
}

function relation(kind: EntityKind, title: string, link = ''): EntityRelation {
  return { title, kind, kindLabel: KIND_LABEL[kind], link };
}

export function getEntityMeta(item: EntityItem): EntityMeta {
  const domain = getPublicDomain();
  // Comments live on their parent post's page, so link to the post's id rather than the comment's own id.
  const postId = item.post?.id || item.id;
  const link = domain && postId ? `${domain}community/post/${postId}` : '';

  if (item.parentComment) {
    const reply = relation('reply', '');

    return { ...reply, link: '', relations: [reply] };
  }

  const relations: EntityRelation[] = [];

  // Prefer attached relations over `entity` — a post can have article and/or business
  // even when entity is stale (e.g. entity: "article" with article: null).
  if (item.article) {
    relations.push(relation('article', item.article.name || 'Untitled article', link));
  }

  if (item.business) {
    const businessLink = domain && item.business.slug
      ? `${domain}community/businesses/${item.business.slug}`
      : '';

    relations.push(relation(
      'business',
      item.business.name || 'Untitled business',
      businessLink,
    ));
  }

  if (relations.length === 0) {
    switch (item.entity) {
      case 'subcategory':
        relations.push(relation(
          'subcategory',
          item.subCategory?.header || 'Unknown subcategory',
          link,
        ));
        break;
      case 'category':
        relations.push(relation('category', item.category?.name || 'Unknown category', link));
        break;
      case 'article':
      case 'business':
        // entity claims a relation but nothing is attached — keep UI empty
        break;
      case 'international_community':
        relations.push(relation(
          'international_community',
          item.international?.country?.name || 'International Community',
          link,
        ));
        break;
      case 'post': {
        let postTitle = 'Unnamed post';

        if (item.post?.text) {
          postTitle = item.post.text.length > 20
            ? `${item.post.text.slice(0, 20)}...`
            : item.post.text;
        }

        relations.push(relation('post', postTitle, link));
        break;
      }
      default:
        break;
    }
  }

  const primary = relations[0] || relation('unknown', '');

  return {
    title: primary.title,
    kind: primary.kind,
    kindLabel: primary.kindLabel,
    link,
    relations,
  };
}
